import { TeamMember, Testimonial } from './types';

export interface HeroSlide {
  title: string;
  subtitle: string;
  image: string;
  cta: { text: string; link: string };
}

export interface ServiceItem {
  icon: string;
  title: string;
  description: string;
  link: string;
  linkText: string;
}

export interface ProcessStep {
  icon: string;
  title: string;
  description: string;
}

export interface FAQ {
  question: string;
  answer: string;
}

export interface ValueItem {
  icon: string;
  title: string;
  description: string;
}

export interface MarketingItem {
  icon: string;
  title: string;
  description: string;
}

export interface SiteContent {
  pages: {
    home: {
      sections: {
        hero: {
          tagline: string;
          slides: HeroSlide[];
          contactButton: {
            text: string;
            link: string;
          };
        };
        services: {
          heading: string;
          subheading: string;
          items: ServiceItem[];
        };
        about: {
          heading: string;
          paragraphs: string[];
          image: string;
          ctaText: string;
          ctaLink: string;
        };
        testimonials: {
          heading: string;
          subheading: string;
        };
        cta: {
          heading: string;
          subheading: string;
          ctaText: string;
          ctaLink: string;
        };
        listings: {
          heading: string;
          subheading: string;
          ctaText: string;
          ctaLink: string;
        };
      };
    };
    about: {
      sections: {
        hero: {
          subtitle: string;
          heading: string;
          description: string;
          image: string;
        };
        story: {
          heading: string;
          paragraphs: string[];
        };
        team: {
          heading: string;
          subheading: string;
        };
        values: {
          heading: string;
          subheading: string;
          items: ValueItem[];
        };
        cta: {
          heading: string;
          subheading: string;
          ctaText: string;
          ctaLink: string;
        };
      };
    };
    buyers: {
      sections: {
        hero: {
          subtitle: string;
          heading: string;
          description: string;
          image: string;
          ctaText: string;
          ctaLink: string;
        };
        tips: {
          heading: string;
          paragraphs: string[];
          benefitsTitle: string;
          benefits: string[];
        };
        process: {
          heading: string;
          subheading: string;
          steps: ProcessStep[];
        };
        whyUs: {
          heading: string;
          benefits: string[];
        };
        firstTimeBuyer: {
          heading: string;
          paragraphs: string[];
          ctaText: string;
          ctaLink: string;
        };
        faq: {
          heading: string;
          items: FAQ[];
        };
        contact: {
          title: string;
          description: string;
        };
      };
    };
    sellers: {
      sections: {
        hero: {
          subtitle: string;
          heading: string;
          description: string;
          image: string;
          ctaText: string;
          ctaLink: string;
        };
        tips: {
          heading: string;
          paragraphs: string[];
          benefitsTitle: string;
          benefits: string[];
        };
        process: {
          heading: string;
          subheading: string;
          steps: ProcessStep[];
        };
        marketing: {
          heading: string;
          subheading: string;
          items: MarketingItem[];
        };
        whyUs: {
          heading: string;
          description: string;
          benefits: string[];
        };
        valuation: {
          heading: string;
          description: string;
          features: string[];
          ctaText: string;
          ctaLink: string;
        };
        contact: {
          title: string;
          description: string;
        };
      };
    };
    contact: {
      sections: {
        hero: {
          subtitle: string;
          heading: string;
          description: string;
          image: string;
        };
        info: {
          address: {
            icon: string;
            title: string;
            line1: string;
            line2: string;
          };
          phone: {
            icon: string;
            title: string;
            number: string;
          };
          email: {
            icon: string;
            title: string;
            address: string;
          };
        };
        form: {
          title: string;
          description: string;
        };
        team: {
          heading: string;
          subheading: string;
        };
      };
    };
    listings: {
      sections: {
        hero: {
          subtitle: string;
          heading: string;
          description: string;
          image: string;
        };
        active: {
          heading: string;
          subheading: string;
          emptyState: {
            icon: string;
            heading: string;
            description: string;
            ctaText: string;
            ctaLink: string;
          };
        };
        sold: {
          heading: string;
          subheading: string;
        };
        cta: {
          heading: string;
          description: string;
          primaryCtaText: string;
          primaryCtaLink: string;
          secondaryCtaText: string;
          secondaryCtaLink: string;
        };
      };
    };
  };
  global: {
    team: TeamMember[];
    testimonials: Testimonial[];
    tagline: string;
    companyName: string;
    address: {
      street: string;
      city: string;
      state: string;
      zip: string;
    };
    phone: string;
    email: string;
    nav: {
      home: string;
      about: string;
      buyers: string;
      sellers: string;
      listings: string;
      search: string;
      contact: string;
    };
    footer: {
      tagline: string;
      description: string;
      quickLinksHeading: string;
      quickLinks: { label: string; link: string }[];
      servicesHeading: string;
      services: { label: string; link: string }[];
      contactHeading: string;
    };
  };
}

const siteContent: SiteContent = {
  "pages": {
    "home": {
      "sections": {
        "cta": {
          "ctaLink": "/contact",
          "ctaText": "Schedule a Consultation",
          "heading": "Ready to Get Started?",
          "subheading": "Let's discuss your real estate goals and create a plan to achieve them."
        },
        "hero": {
          "slides": [
            {
              "cta": {
                "link": "/listings",
                "text": "Start Your Search"
              },
              "image": "/media/hero-1.jpg",
              "title": "Find Your Dream Home",
              "subtitle": "Expert guidance for buyers in Montgomery County"
            },
            {
              "cta": {
                "link": "/sellers",
                "text": "List Your Home"
              },
              "image": "/media/hero-2.png",
              "title": "Sell With Confidence",
              "subtitle": "Get top dollar with our proven strategies"
            },
            {
              "cta": {
                "link": "/about",
                "text": "Meet Our Team"
              },
              "image": "/media/hero-3.png",
              "title": "Local Experts, Real Results",
              "subtitle": "Your trusted real estate team in Royersford, PA"
            }
          ],
          "tagline": "Our KREW works for YOU!",
          "contactButton": {
            "text": "Contact Us",
            "link": "/contact"
          }
        },
        "about": {
          "image": "/media/keithkline.jpg",
          "ctaLink": "/about",
          "ctaText": "Meet Our Team",
          "heading": "Your Local Real Estate Experts",
          "paragraphs": [
            "At Kline Real Estate Works, we believe that buying or selling a home should be an exciting journey, not a stressful one. Our team of dedicated professionals brings years of local market expertise and a commitment to exceptional service.",
            "Whether you're a first-time homebuyer, looking to upgrade, or ready to sell, we're here to guide you through every step of the process with honesty, integrity, and personalized attention."
          ]
        },
        "listings": {
          "heading": "Recently Sold",
          "subheading": "A look at some of our recent successful transactions",
          "ctaText": "View All Listings",
          "ctaLink": "/listings"
        },
        "services": {
          "items": [
            {
              "icon": "home_work",
              "link": "/buyers",
              "title": "Buying",
              "linkText": "Learn More",
              "description": "Find your perfect home with personalized guidance and expert market knowledge."
            },
            {
              "icon": "sell",
              "link": "/sellers",
              "title": "Selling",
              "linkText": "Learn More",
              "description": "Get top dollar for your property with our proven marketing strategies."
            },
            {
              "icon": "trending_up",
              "link": "/contact",
              "title": "Investing",
              "linkText": "Get Started",
              "description": "Build wealth through strategic real estate investments with expert guidance."
            }
          ],
          "heading": "How We Can Help",
          "subheading": "Whether you're buying, selling, or investing, our team is here to guide you every step of the way"
        },
        "testimonials": {
          "heading": "What Our Clients Say",
          "subheading": "Don't just take our word for it - hear from our satisfied clients"
        }
      }
    },
    "about": {
      "sections": {
        "cta": {
          "ctaLink": "/contact",
          "ctaText": "Get in Touch",
          "heading": "Let's Work Together",
          "subheading": "Ready to start your real estate journey? We'd love to hear from you."
        },
        "hero": {
          "image": "/media/hero-2.png",
          "heading": "Meet Your Real Estate Team",
          "subtitle": "About Us",
          "description": "Dedicated professionals committed to making your real estate dreams a reality"
        },
        "team": {
          "heading": "Our Team",
          "subheading": "Meet the dedicated professionals ready to help you achieve your real estate goals"
        },
        "story": {
          "heading": "Our Story",
          "paragraphs": [
            "Kline Real Estate Works was founded on a simple principle: every client deserves exceptional service, honest guidance, and a real estate experience that exceeds expectations.",
            "Based in Royersford, PA, our team brings together years of local market expertise with a deep commitment to our community. We don't just sell houses—we help families find their homes and build their futures.",
            "What sets us apart is our hands-on approach. From the first conversation to closing day and beyond, we're with you every step of the way. Our clients become our friends, and their success is our greatest reward."
          ]
        },
        "values": {
          "items": [
            {
              "icon": "handshake",
              "title": "Integrity",
              "description": "We believe in honest, transparent communication. You'll always know where you stand with us."
            },
            {
              "icon": "workspace_premium",
              "title": "Excellence",
              "description": "We strive for excellence in every transaction, going above and beyond for our clients."
            },
            {
              "icon": "groups",
              "title": "Community",
              "description": "We're proud to serve the Royersford area and help strengthen our local community."
            }
          ],
          "heading": "Our Values",
          "subheading": "The principles that guide everything we do"
        }
      }
    },
    "buyers": {
      "sections": {
        "faq": {
          "items": [
            {
              "answer": "Down payment requirements vary based on the loan type. Some loans require as little as 3-3.5% down, while others may require 10-20%. We can connect you with lenders who can explain your options.",
              "question": "How much do I need for a down payment?"
            },
            {
              "answer": "Yes! Getting pre-approved helps you understand your budget and makes your offer more competitive. Sellers prefer working with pre-approved buyers because it reduces the risk of financing falling through.",
              "question": "Should I get pre-approved before looking at homes?"
            },
            {
              "answer": "Typically 30-60 days from accepted offer to closing. However, the home search itself can take anywhere from a few weeks to several months depending on the market and your specific needs.",
              "question": "How long does the buying process take?"
            },
            {
              "answer": "Plan for closing costs (2-5% of purchase price), home inspection, appraisal, and moving expenses. We'll provide a detailed breakdown of expected costs early in the process.",
              "question": "What costs should I budget for beyond the purchase price?"
            }
          ],
          "heading": "Frequently Asked Questions"
        },
        "hero": {
          "image": "/media/hero-3.png",
          "ctaLink": "/listings",
          "ctaText": "View Listings",
          "heading": "Find Your Perfect Home",
          "subtitle": "For Buyers",
          "description": "Expert guidance and personalized service to help you navigate the home buying process"
        },
        "tips": {
          "heading": "How We Help You Buy a Home",
          "benefits": [
            "Be more likely to find the home that meets all your criteria",
            "Lessen the amount of time it will take to find your home",
            "Understand all the terms, processes and documents used when buying your home",
            "Have up-to-date market information that will allow you to make informed decisions",
            "Have a skilled negotiator working on your behalf, one who is committed to looking after your best interests",
            "Enjoy peace of mind, knowing that all the details of your purchase are being taken care of by an experienced and knowledgeable professional"
          ],
          "paragraphs": [
            "Whether it's your first home or if you're an experienced buyer, purchasing a home is a complex process. By working with us, you'll find a home in the neighborhood where you want to live, one that fits your budget and meets your goals for features, quality and value.",
            "Once you've found the home that's right for you, we will guide you through the process of making an offer, negotiating the terms of the sale, getting your potential purchase inspected, repaired and appraised, and closing the sale."
          ],
          "benefitsTitle": "When You Work With Us, You Will:"
        },
        "whyUs": {
          "heading": "Why Work With Us?",
          "benefits": [
            "Local market expertise - we know the neighborhoods",
            "Access to exclusive and off-market listings",
            "Skilled negotiators working in your best interest",
            "Network of trusted lenders, inspectors, and contractors",
            "Available when you need us - evenings and weekends",
            "No pressure, just honest guidance"
          ]
        },
        "contact": {
          "title": "Ready to Start Your Home Search?",
          "description": "Tell us about your ideal home and we'll help you find it."
        },
        "process": {
          "steps": [
            {
              "icon": "search",
              "title": "1. Discovery",
              "description": "We start by understanding your needs, preferences, budget, and timeline to create a personalized search plan."
            },
            {
              "icon": "home",
              "title": "2. Home Search",
              "description": "We'll search for properties that match your criteria and schedule viewings at times convenient for you."
            },
            {
              "icon": "description",
              "title": "3. Offer & Negotiation",
              "description": "We'll help you craft a competitive offer and negotiate on your behalf to get the best possible terms."
            },
            {
              "icon": "fact_check",
              "title": "4. Due Diligence",
              "description": "We'll guide you through inspections, appraisals, and all the paperwork to ensure a smooth transaction."
            },
            {
              "icon": "key",
              "title": "5. Closing",
              "description": "We'll coordinate with all parties to ensure a seamless closing and hand you the keys to your new home."
            },
            {
              "icon": "support_agent",
              "title": "6. Ongoing Support",
              "description": "Our relationship doesn't end at closing. We're here for any questions or needs that arise after you move in."
            }
          ],
          "heading": "The Buying Process",
          "subheading": "We make home buying simple and stress-free with our proven approach"
        },
        "firstTimeBuyer": {
          "ctaLink": "/contact",
          "ctaText": "Learn More",
          "heading": "First-Time Buyer?",
          "paragraphs": [
            "Buying your first home is exciting, but it can also feel overwhelming. We specialize in helping first-time buyers navigate the process with confidence.",
            "From understanding your financing options to finding the right neighborhood, we'll be by your side every step of the way."
          ]
        }
      }
    },
    "contact": {
      "sections": {
        "form": {
          "title": "Send Us a Message",
          "description": "Fill out the form below and we'll get back to you as soon as possible."
        },
        "hero": {
          "image": "/media/hero-1.jpg",
          "heading": "Let's Start a Conversation",
          "subtitle": "Contact Us",
          "description": "We're here to help with all your real estate needs"
        },
        "info": {
          "email": {
            "icon": "email",
            "title": "Email Us",
            "address": "contact@keithklinegroup.com"
          },
          "phone": {
            "icon": "phone",
            "title": "Call Us",
            "number": "(610) 948-5739"
          },
          "address": {
            "icon": "location_on",
            "line1": "10 N. 5th Ave. Fl 1",
            "line2": "Royersford, PA 19468",
            "title": "Visit Us"
          }
        },
        "team": {
          "heading": "Meet Our Team",
          "subheading": "Reach out directly to any of our team members"
        }
      }
    },
    "sellers": {
      "sections": {
        "hero": {
          "image": "/media/hero-1.jpg",
          "ctaLink": "/contact",
          "ctaText": "Get Your Free Valuation",
          "heading": "Sell Your Home With Confidence",
          "subtitle": "For Sellers",
          "description": "Strategic marketing and expert guidance to get you the best price for your property"
        },
        "tips": {
          "heading": "How We Help You Sell Your Home",
          "benefits": [
            "Be more likely to realize the highest return on the sale of your home",
            "Reduce the number of days your home is on the market",
            "Become familiar with all the terms, processes and paperwork involved in selling your home",
            "Have confidence that your home will enjoy exposure to more buyers and agents with qualified buyers",
            "Receive regularly updated market information that will enable you to make informed decisions",
            "Have a skilled negotiator working on your behalf",
            "Have peace of mind knowing that all the details of your sale are being handled properly by a licensed and trained professional"
          ],
          "paragraphs": [
            "Helping find a buyer for your home is only one facet of a real estate professional's job. There are many more aspects including explaining the basic real estate practices and principles and all the related paperwork, performing a Comparative Market Analysis (CMA) to help determine your home's value, helping to prepare your home for sale, listing and marketing your home, and keeping you informed throughout the process.",
            "When the sale begins, it's important to work with someone who will assist you with the paperwork, negotiate on your behalf, make sure deadlines are met, and work with the escrow company to ensure a smooth closing."
          ],
          "benefitsTitle": "By Working With Us, You Will:"
        },
        "whyUs": {
          "heading": "Why Sellers Choose Us",
          "benefits": [
            "Accurate pricing based on deep local market knowledge",
            "Professional staging and photography included",
            "Aggressive marketing that reaches qualified buyers",
            "Skilled negotiation to maximize your return",
            "Transparent communication throughout the process",
            "We go above and beyond - even mowing lawns!"
          ],
          "description": "Selling your home is a significant decision, and you deserve a team that treats it with the importance it deserves. Here's what sets us apart:"
        },
        "contact": {
          "title": "Ready to Sell?",
          "description": "Tell us about your property and we'll get back to you with a market analysis."
        },
        "process": {
          "steps": [
            {
              "icon": "analytics",
              "title": "1. Market Analysis",
              "description": "We'll analyze your home and the local market to determine the optimal listing price strategy."
            },
            {
              "icon": "home_repair_service",
              "title": "2. Preparation",
              "description": "We'll advise on improvements and staging to maximize your home's appeal to buyers."
            },
            {
              "icon": "campaign",
              "title": "3. Marketing",
              "description": "Professional photos, virtual tours, and strategic marketing to reach qualified buyers."
            },
            {
              "icon": "event",
              "title": "4. Showings",
              "description": "We coordinate and host showings, providing feedback and keeping you informed."
            },
            {
              "icon": "gavel",
              "title": "5. Negotiation",
              "description": "Expert negotiation to secure the best terms and highest price for your property."
            },
            {
              "icon": "celebration",
              "title": "6. Closing",
              "description": "We manage all the details to ensure a smooth closing and successful sale."
            }
          ],
          "heading": "Our Selling Process",
          "subheading": "A proven approach to get your home sold quickly and for the best price"
        },
        "marketing": {
          "items": [
            {
              "icon": "photo_camera",
              "title": "Professional Photography",
              "description": "High-quality photos that showcase your home's best features"
            },
            {
              "icon": "videocam",
              "title": "Video Tours",
              "description": "Engaging video content to attract online buyers"
            },
            {
              "icon": "computer",
              "title": "Online Presence",
              "description": "Listed on all major real estate platforms and social media"
            },
            {
              "icon": "mail",
              "title": "Email Marketing",
              "description": "Targeted campaigns to our network of active buyers"
            },
            {
              "icon": "groups",
              "title": "Open Houses",
              "description": "Strategic open houses to generate interest and offers"
            },
            {
              "icon": "share",
              "title": "Social Media",
              "description": "Promoted posts reaching thousands of potential buyers"
            }
          ],
          "heading": "How We Market Your Home",
          "subheading": "Comprehensive marketing to reach the right buyers"
        },
        "valuation": {
          "ctaLink": "/contact",
          "ctaText": "Request Valuation",
          "heading": "Free Home Valuation",
          "features": [
            "Comparative market analysis",
            "Local market trends",
            "No obligation"
          ],
          "description": "Wondering what your home is worth in today's market? We'll provide a comprehensive market analysis at no cost or obligation."
        }
      }
    },
    "listings": {
      "sections": {
        "cta": {
          "heading": "Don't See What You're Looking For?",
          "description": "Our team has access to listings before they hit the market. Tell us what you're looking for and we'll find it.",
          "primaryCtaLink": "/contact",
          "primaryCtaText": "Contact Us",
          "secondaryCtaLink": "/buyers",
          "secondaryCtaText": "Buyer Resources"
        },
        "hero": {
          "image": "/media/hero-2.png",
          "heading": "Find Your Next Home",
          "subtitle": "Property Listings",
          "description": "Browse our listings and recent sales in the Royersford area"
        },
        "sold": {
          "heading": "Recently Sold",
          "subheading": "A look at some of our recent successful transactions"
        },
        "active": {
          "heading": "Available Properties",
          "emptyState": {
            "icon": "home_work",
            "ctaLink": "/contact",
            "ctaText": "Get Started",
            "heading": "Looking for a Home?",
            "description": "We have access to the latest listings as soon as they hit the market. Contact us to get personalized listings sent directly to you."
          },
          "subheading": "Browse our current listings"
        }
      }
    }
  },
  "global": {
    "nav": {
      "home": "Home",
      "about": "About",
      "buyers": "Buyers",
      "search": "Search",
      "contact": "Contact",
      "sellers": "Sellers",
      "listings": "Listings"
    },
    "team": [
      {
        "id": "keith-kline",
        "bio": "As the founder of Kline Real Estate Works, Keith brings years of experience and a commitment to excellence in every transaction. His hands-on approach and deep knowledge of the local market ensure clients receive personalized service from start to finish.",
        "name": "Keith Kline",
        "role": "BROKER / OWNER",
        "email": "keith@keithklinegroup.com",
        "image": "/media/keithkline.jpg",
        "phone": "(610) 213-1473"
      },
      {
        "id": "jess",
        "bio": "Jess is known for her honest, no-nonsense approach with a soft touch when necessary. Her extensive knowledge and expertise in real estate makes every transaction a great experience for her clients.",
        "name": "Jess Fontaine-Burgess",
        "role": "REALTOR®",
        "email": "jess@keithklinegroup.com",
        "image": "/media/jesicafontaineburgess.jpg",
        "phone": "(610) 597-3666"
      },
      {
        "id": "olivia-bozzelli",
        "bio": "Olivia brings fresh energy and dedication to the team. Her attention to detail and commitment to client satisfaction make her an invaluable asset to buyers and sellers alike.",
        "name": "Olivia Bozzelli",
        "role": "REALTOR®",
        "email": "olivia@keithklinegroup.com",
        "image": "/media/oliviabozzelli.jpg",
        "phone": "(484) 620-1937"
      },
      {
        "id": "keith-gerhard",
        "bio": "Keith Gerhard combines market expertise with a genuine passion for helping clients find their perfect home. His dedication to understanding each client's unique needs ensures a smooth and successful real estate journey.",
        "name": "Keith Gerhard",
        "role": "REALTOR®",
        "email": "keithg@keithklinegroup.com",
        "image": "/media/keithgerhard.jpg",
        "phone": "(610) 960-9038"
      }
    ],
    "email": "contact@keithklinegroup.com",
    "phone": "(610) 948-5739",
    "footer": {
      "tagline": "Our KREW works for YOU!",
      "quickLinksHeading": "Quick Links",
      "servicesHeading": "Services",
      "contactHeading": "Contact Us",
      "services": [
        {
          "link": "/buyers",
          "label": "Buyer Representation"
        },
        {
          "link": "/sellers",
          "label": "Seller Representation"
        },
        {
          "link": "/listings",
          "label": "Property Search"
        },
        {
          "link": "/contact",
          "label": "Free Consultation"
        }
      ],
      "quickLinks": [
        {
          "link": "/",
          "label": "Home"
        },
        {
          "link": "/about",
          "label": "About Us"
        },
        {
          "link": "/buyers",
          "label": "Buyers"
        },
        {
          "link": "/sellers",
          "label": "Sellers"
        },
        {
          "link": "/listings",
          "label": "Listings"
        },
        {
          "link": "/contact",
          "label": "Contact"
        }
      ],
      "description": "Your trusted real estate team in Royersford, PA. We're dedicated to providing exceptional service and expert guidance for all your real estate needs."
    },
    "address": {
      "zip": "19468",
      "city": "Royersford",
      "state": "PA",
      "street": "10 N. 5th Ave. Fl 1"
    },
    "tagline": "Our KREW works for YOU!",
    "companyName": "Kline Real Estate Works",
    "testimonials": [
      {
        "id": "1",
        "text": "Jess was excellent from start to finish. She has extensive knowledge and expertise in real estate which was evident in all of our interactions. I will describe her sales style as honest, no nonsense with a soft touch when necessary, which I think is exceptional. She made it a great experience.",
        "author": "Eric H.",
        "rating": 5,
        "agentName": "Jessica Fontaine-Burgess"
      },
      {
        "id": "2",
        "text": "Keith is an excellent realtor. He is more than knowledgeable about this market and talks you through the entire process. His professionalism is exceptional while being very down to earth. He goes out of his way to make sure your house is sellable, even to the point of mowing your lawn when your landscapers were a bit behind schedule. I would highly recommend Keith and his team!!",
        "author": "Joy L.",
        "rating": 5,
        "agentName": "Keith Kline"
      },
      {
        "id": "3",
        "text": "I have bought and sold a lot of houses. I have never ever had a realtor go above and beyond like Keith. The transition was so smooth and quick. Will never use another realtor but Keith.",
        "author": "Scott F.",
        "rating": 5,
        "agentName": "Keith Kline"
      }
    ]
  }
};

export default siteContent;
